<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Auth from 'dashboard/api/auth';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { setColorTheme } from 'dashboard/helper/themeHelper.js';
import Avatar from 'next/avatar/Avatar.vue';
import Icon from 'next/icon/Icon.vue';
import Logo from 'next/icon/Logo.vue';
import AccountSwitcherList from 'next/sidebar/AccountSwitcherList.vue';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';

defineProps({
  // Shown on the isolated PowerApp pages, hidden on the Home page itself.
  showBackToHome: { type: Boolean, default: false },
  title: { type: String, default: '' },
});

const { t, locale } = useI18n();
const { currentAccount, accountScopedRoute } = useAccount();
const { enabledLanguages } = useConfig();
const { updateUISettings } = useUISettings();
const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');

// A lista (ordem, busca e favoritas) é a mesma da sidebar: AccountSwitcherList.
const canSwitchAccount = computed(
  () => (currentUser.value.accounts || []).length > 1
);

// Full page load, not a router push: switching org rebuilds the whole store.
const onChangeAccount = id => {
  window.location.href = `/app/accounts/${id}/home`;
};

const languages = computed(() => enabledLanguages || []);

// Nesta arvore os locales sao carregados estaticamente por dashboard/i18n, entao
// basta trocar. Na arvore do sandbox eles sao code-split e a troca precisa de um
// `await ensureLocale(code)` antes desta linha, senao a tela pisca em ingles.
const onChangeLanguage = async code => {
  if (code === locale.value) return;
  try {
    await updateUISettings({ locale: code });
    locale.value = code;
  } catch (error) {
    useAlert(t('OS_HOME.LANGUAGE.UPDATE_ERROR'));
  }
};

// Mesmo formato do useAppearanceHotKeys: o label ja resolvido, nao a chave.
const themes = computed(() => [
  { key: 'light', label: t('OS_HOME.THEME.LIGHT'), icon: 'i-lucide-sun' },
  { key: 'dark', label: t('OS_HOME.THEME.DARK'), icon: 'i-lucide-moon' },
  { key: 'auto', label: t('OS_HOME.THEME.SYSTEM'), icon: 'i-lucide-monitor' },
]);

// 'auto' e o default do themeHelper: seguir o sistema, e nao um terceiro tema.
const colorScheme = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'auto'
);
const isDark = ref(document.body.classList.contains('dark'));

const onChangeTheme = key => {
  LocalStorage.set(LOCAL_STORAGE_KEYS.COLOR_SCHEME, key);
  setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
  colorScheme.value = key;
  isDark.value = document.body.classList.contains('dark');
};

const profileItems = computed(() => [
  {
    label: t('OS_HOME.MENU.PROFILE'),
    icon: 'i-lucide-user-pen',
    link: accountScopedRoute('profile_settings_index'),
  },
  {
    label: t('OS_HOME.MENU.LOGOUT'),
    icon: 'i-lucide-power',
    click: Auth.logout,
  },
]);
</script>

<template>
  <header
    class="flex flex-shrink-0 gap-2 items-center px-3 h-14 border-b sm:gap-3 sm:px-6 border-n-weak bg-n-solid-1"
  >
    <router-link
      v-if="showBackToHome"
      :to="accountScopedRoute('os_home')"
      class="grid place-content-center size-8 rounded-lg text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
      :title="t('OS_HOME.BACK')"
    >
      <Icon icon="i-lucide-arrow-left" class="size-4" />
    </router-link>

    <router-link
      :to="accountScopedRoute('os_home')"
      class="flex gap-2 items-center min-w-0"
    >
      <Logo class="flex-shrink-0 size-7" />
      <span
        class="hidden text-sm font-semibold truncate sm:inline text-n-slate-12"
      >
        {{ t('OS_HOME.BRAND') }}
      </span>
    </router-link>

    <span
      v-if="title"
      class="hidden flex-shrink-0 w-px h-4 sm:block bg-n-strong"
    />
    <span v-if="title" class="text-sm truncate text-n-slate-11">
      {{ title }}
    </span>

    <div class="flex flex-1 gap-1 justify-end items-center min-w-0 sm:gap-3">
      <DropdownContainer class="relative">
        <template #trigger="{ toggle, isOpen }">
          <button
            class="flex gap-1 items-center px-2 py-1 min-w-0 rounded-lg sm:gap-2 max-w-32 sm:max-w-56"
            :class="[
              isOpen && 'bg-n-alpha-1',
              canSwitchAccount
                ? 'cursor-pointer hover:bg-n-alpha-1'
                : 'cursor-default',
            ]"
            :title="currentAccount.name"
            @click="() => canSwitchAccount && toggle()"
          >
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ currentAccount.name }}
            </span>
            <Icon
              v-if="canSwitchAccount"
              icon="i-lucide-chevron-down"
              class="flex-shrink-0 size-4 text-n-slate-10"
            />
          </button>
        </template>
        <!-- No celular a caixa ancorava na borda esquerda do gatilho e saía da tela pela
             esquerda; ali ela fica fixa sob o topo, com a largura da tela. -->
        <DropdownBody
          v-if="canSwitchAccount"
          class="z-50 mt-2 w-56 sm:w-64 top-full sm:ltr:right-0 sm:rtl:left-0 max-sm:fixed max-sm:inset-x-4 max-sm:top-14 max-sm:w-auto"
        >
          <AccountSwitcherList
            :titulo-das-demais="t('OS_HOME.SWITCH_ORG')"
            @trocar="onChangeAccount"
          />
        </DropdownBody>
      </DropdownContainer>

      <DropdownContainer class="relative flex-shrink-0">
        <template #trigger="{ toggle, isOpen }">
          <button
            class="grid place-content-center rounded-lg cursor-pointer size-8 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
            :class="{ 'bg-n-alpha-1': isOpen }"
            :title="t('OS_HOME.LANGUAGE.TITLE')"
            :aria-label="t('OS_HOME.LANGUAGE.TITLE')"
            @click="toggle"
          >
            <Icon icon="i-lucide-languages" class="size-4" />
          </button>
        </template>
        <DropdownBody class="z-50 mt-2 w-56 top-full ltr:right-0 rtl:left-0">
          <DropdownSection
            :title="t('OS_HOME.LANGUAGE.TITLE')"
            height="max-h-80"
          >
            <DropdownItem
              v-for="language in languages"
              :key="language.iso_639_1_code"
              class="cursor-pointer"
              @click="onChangeLanguage(language.iso_639_1_code)"
            >
              <template #label>
                <span class="truncate text-n-slate-12">
                  {{ language.name }}
                </span>
                <Icon
                  v-show="language.iso_639_1_code === locale"
                  icon="i-lucide-check"
                  class="flex-shrink-0 size-4 text-n-teal-11"
                />
              </template>
            </DropdownItem>
          </DropdownSection>
        </DropdownBody>
      </DropdownContainer>

      <DropdownContainer class="relative flex-shrink-0">
        <template #trigger="{ toggle, isOpen }">
          <button
            class="grid place-content-center rounded-lg cursor-pointer size-8 text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
            :class="{ 'bg-n-alpha-1': isOpen }"
            :title="t('OS_HOME.THEME.TITLE')"
            :aria-label="t('OS_HOME.THEME.TITLE')"
            @click="toggle"
          >
            <Icon
              :icon="isDark ? 'i-lucide-moon' : 'i-lucide-sun'"
              class="size-4"
            />
          </button>
        </template>
        <DropdownBody class="z-50 mt-2 w-48 top-full ltr:right-0 rtl:left-0">
          <DropdownSection :title="t('OS_HOME.THEME.TITLE')">
            <DropdownItem
              v-for="theme in themes"
              :key="theme.key"
              class="cursor-pointer"
              @click="onChangeTheme(theme.key)"
            >
              <template #label>
                <Icon :icon="theme.icon" class="size-4 text-n-slate-11" />
                <span class="flex-1 truncate text-n-slate-12">
                  {{ theme.label }}
                </span>
                <Icon
                  v-show="theme.key === colorScheme"
                  icon="i-lucide-check"
                  class="flex-shrink-0 size-4 text-n-teal-11"
                />
              </template>
            </DropdownItem>
          </DropdownSection>
        </DropdownBody>
      </DropdownContainer>

      <DropdownContainer class="relative flex-shrink-0">
        <template #trigger="{ toggle, isOpen }">
          <button
            class="grid place-content-center p-1 rounded-lg cursor-pointer hover:bg-n-alpha-1"
            :class="{ 'bg-n-alpha-1': isOpen }"
            :title="currentUser.available_name"
            @click="toggle"
          >
            <Avatar
              :size="32"
              :name="currentUser.available_name"
              :src="currentUser.avatar_url"
              :status="currentUserAvailability"
              rounded-full
            />
          </button>
        </template>
        <DropdownBody class="z-50 mt-2 w-56 top-full ltr:right-0 rtl:left-0">
          <DropdownItem
            v-for="item in profileItems"
            :key="item.label"
            v-bind="item"
          />
        </DropdownBody>
      </DropdownContainer>
    </div>
  </header>
</template>
